import { useRFS, useSupportingDocumentsReview } from "@/api/rfs";
import { useReviewStatusUpdater } from "@/features/opm/utils/rfsStatusUpdater";
import { REQUIRED_MESSAGE } from "@/utils/constant";
import { formatDateWithIntl } from "@/utils/helper/formatDate";
import {
  swalError,
  swalLoading,
  swalSuccess,
  swalWarningDefault,
} from "@/utils/lib/swal";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

const docSchema = z.enum([
  "opm",
  "evidenceNn",
  "hptar",
  "gpon",
  "abdFromKmz",
  "abdFromDwg",
  "abd",
  "evidenceFeeder",
  "bakp",
  "cmld",
  "clampHook",
  "evidenceOlt",
]);

type DocType = z.infer<typeof docSchema>;

const reviewFormSchema = z
  .object({
    decision: z.enum(["yes", "no", "cancel"]),
    notes: z.string().optional(),
    problematicDocuments: z.array(docSchema).optional(),
    statusFeederAndOlt: z.string().optional(),
  })
  .refine(
    (val) => {
      if ((val.decision === "no" || val.decision === "cancel") && !val.notes) {
        return false;
      }
      return true;
    },
    {
      message: REQUIRED_MESSAGE,
      path: ["notes"],
    },
  )
  .refine(
    (val) => {
      if (val.decision === "no" && !val.problematicDocuments?.length) {
        return false;
      }
      return true;
    },
    {
      message: REQUIRED_MESSAGE,
      path: ["problematicDocuments"],
    },
  );

type ReviewFormSchema = z.infer<typeof reviewFormSchema>;

function useDocumentReview({ id }: { id: string }) {
  const navigate = useNavigate();
  const { mutate: review } = useSupportingDocumentsReview();
  const { data: rfs } = useRFS({ id });
  const { updateDocumentStatus } = useReviewStatusUpdater();

  function handleReview(values: ReviewFormSchema) {
    swalWarningDefault
      .fire({
        text: `This Action cannot be undone. Are you sure want to ${getDecisionLabel(
          values.decision,
        )} this RFS?`,
      })
      .then((result) => {
        if (result.isConfirmed) {
          swalLoading.fire();
          /**
           * Validate payload before send to API
           * - If decision is 'yes', remove notes and problematicDocuments
           * - If decision is 'cancel', remove problematicDocuments
           */
          if (values.decision === "yes") {
            values.notes = undefined;
            values.problematicDocuments = undefined;
          } else if (values.decision === "cancel") {
            values.problematicDocuments = undefined;
          }

          reviewMutation(values, () =>
            updateDocumentStatus({
              rfsId: id,
              problematicDocuments: mapProblematicDocumentsToStatus(
                values.problematicDocuments,
              ),
              rfsData: rfs,
            }),
          );
        }
      });
  }

  function reviewMutation(values: ReviewFormSchema, onSuccess?: () => void) {
    review(
      {
        id,
        data: {
          notes: values.notes,
          opmFdt: {
            revise: values.problematicDocuments?.includes("opm") ?? false,
          },
          nnEvidence: {
            revise:
              values.problematicDocuments?.includes("evidenceNn") ?? false,
          },
          hptar: {
            revise: values.problematicDocuments?.includes("hptar") ?? false,
          },
          gpon: {
            revise: values.problematicDocuments?.includes("gpon") ?? false,
          },
          supportingDocument: {
            abdDariKmzIsRevise:
              values.problematicDocuments?.includes("abdFromKmz") ?? false,
            abdDariDwgIsRevise:
              values.problematicDocuments?.includes("abdFromDwg") ?? false,
            abdDwgIsRevise:
              values.problematicDocuments?.includes("abd") ?? false,
            evidenceFeederIsRevise:
              values.problematicDocuments?.includes("evidenceFeeder") ?? false,
            bakpIsRevise:
              values.problematicDocuments?.includes("bakp") ?? false,
            cmldIsRevise:
              values.problematicDocuments?.includes("cmld") ?? false,
            clampHookIsRevise:
              values.problematicDocuments?.includes("clampHook") ?? false,
            evidenceOltIsRevise:
              values.problematicDocuments?.includes("evidenceOlt") ?? false,
          },
        },
        params: {
          decision: values.decision,
          withVariable: "true",
        },
      },
      {
        onSuccess: () => {
          const isCancel = values.decision === "cancel";
          onSuccess?.();
          swalSuccess
            .fire({
              title: isCancel ? "Successfully Cancelled" : "Success",
              text: "RFS Submission process is successful.",
            })
            .then(
              (alertState) =>
                (alertState.isConfirmed || alertState.isDismissed) &&
                navigate(0),
            );
        },
        onError: () => {
          swalError
            .fire({
              text: "RFS Submission process is failed.",
            })
            .then(
              (alertState) =>
                (alertState.isConfirmed || alertState.isDismissed) &&
                navigate(0),
            );
        },
      },
    );
  }

  return {
    handleReview,
  };
}

function getDecisionLabel(value: string) {
  switch (value) {
    case "yes":
      return "approve";
    case "no":
      return "reject";
    case "cancel":
      return "cancel";
    default:
      return "";
  }
}

function mapProblematicDocumentsToStatus(
  problematicDocuments?: DocType[],
): Record<string, string | null> {
  if (!problematicDocuments || problematicDocuments.length === 0) {
    return {};
  }

  const now = new Date();
  const formattedDate = formatDateWithIntl(now);

  return problematicDocuments.reduce(
    (acc, doc) => {
      acc[`${doc}Status`] = "Unvalidated";
      acc[`${doc}UncompletedDate`] = formattedDate;
      acc[`${doc}CompletedDate`] = null;
      return acc;
    },
    {} as Record<string, string | null>,
  );
}

export type { ReviewFormSchema, DocType };
export { reviewFormSchema, useDocumentReview, mapProblematicDocumentsToStatus };
